// 149-cw-r84 (2026-08-27) — Asset export helpers.
// - exportAssetsToExcel: SheetJS .xlsx download
// - printAssetsToPDF: printable HTML window (browser Save-as-PDF)
// - printAssetCardsBulk: 2 kartu per A4 landscape, ukuran proposional (untuk
//   laporan PIC atau tempel di aset)
import * as XLSX from "xlsx";

const CATEGORY_LABEL = {
  office: "Office", vehicle: "Kendaraan", machine: "Mesin", others: "Lainnya",
};
const STATUS_LABEL = {
  active: "Aktif", maintenance: "Maintenance", broken: "Rusak",
  disposed: "Disposed", archived: "Diarsipkan",
};

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};
const fmtIDR = (n) => {
  if (n == null || n === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return "Rp " + num.toLocaleString("id-ID");
};

function buildRow(a, locMap, userMap) {
  const loc = locMap[a.location_id]?.name || a.location_name || "";
  const pic = userMap[a.pic_id]?.name || a.pic_name || "";
  return {
    "No. Asset": a.asset_number || "",
    "Nama": a.name || "",
    "Kategori": CATEGORY_LABEL[a.category] || a.category || "",
    "Brand": a.brand || "",
    "Tipe/Model": a.type_model || "",
    "Serial": a.serial_number || "",
    "Lokasi": loc,
    "PIC": pic,
    "Status": STATUS_LABEL[a.status] || a.status || "",
    "Tgl Pembelian": fmtDate(a.purchase_date),
    "Nilai Pembelian": fmtIDR(a.purchase_value),
    "Interval Pemeriksaan (hari)": a.inspection_interval_days ?? "",
    "Pemeriksaan Terakhir": fmtDate(a.last_inspection_date),
  };
}

export function exportAssetsToExcel(assets, locMap, userMap, opts = {}) {
  const rows = assets.map((a) => buildRow(a, locMap, userMap));
  const title = opts.title || "Inventaris & Aset";
  const meta = `${rows.length} aset · Dibuat ${new Date().toLocaleString("id-ID")}`;
  const header = Object.keys(rows[0] || buildRow({}, {}, {}));
  const aoa = [[title], [meta], [], header, ...rows.map((r) => header.map((h) => r[h]))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = header.map(() => ({ wch: 18 }));
  ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
                   { s: { r: 1, c: 0 }, e: { r: 1, c: header.length - 1 } }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Aset");
  XLSX.writeFile(wb, `${(opts.fileName || "inventaris-aset")}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function printAssetsToPDF(assets, locMap, userMap, opts = {}) {
  const rows = assets.map((a) => buildRow(a, locMap, userMap));
  const title = opts.title || "Inventaris & Aset";
  const subtitle = opts.subtitle || `${rows.length} aset · Dicetak ${new Date().toLocaleString("id-ID")}`;
  const header = Object.keys(rows[0] || buildRow({}, {}, {}));
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #0B1D3A; margin: 0; }
  h1 { font-size: 16px; margin: 0 0 4px 0; }
  .meta { font-size: 10px; color: #64748b; margin-bottom: 8px; }
  table { border-collapse: collapse; width: 100%; font-size: 9px; }
  th, td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #f1f5f9; text-transform: uppercase; letter-spacing: 0.04em; font-size: 8px; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr:nth-child(even) td { background: #f8fafc; }
</style></head><body>
<h1>${esc(title)}</h1>
<div class="meta">${esc(subtitle)}</div>
<table>
  <thead><tr><th style="width:32px;text-align:center;">No.</th>${header.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
  <tbody>
    ${rows.map((r, i) => `<tr><td class="num" style="text-align:center;">${i + 1}</td>${header.map((h) => `<td class="${h.startsWith("Nilai") ? "num" : ""}">${esc(r[h])}</td>`).join("")}</tr>`).join("")}
  </tbody>
</table>
<script>window.onload=()=>{setTimeout(()=>window.print(),200);};</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}

// 2 kartu per A4 landscape (149×105mm/kartu, proposional untuk aset besar).
// Kartu berisi: No. Asset, Nama, Kategori/Brand, Serial, Lokasi, PIC,
// Tgl Beli, Nilai, Interval Pemeriksaan, Pemeriksaan Terakhir.
export function printAssetCardsBulk(assets, locMap, userMap, opts = {}) {
  const cards = assets.map((a) => {
    const loc = locMap[a.location_id]?.name || a.location_name || "-";
    const pic = userMap[a.pic_id]?.name || a.pic_name || "-";
    return {
      no: a.asset_number || "-",
      name: a.name || "-",
      cat: CATEGORY_LABEL[a.category] || a.category || "-",
      brand: [a.brand, a.type_model].filter(Boolean).join(" · ") || "-",
      serial: a.serial_number || "-",
      loc, pic,
      buy: fmtDate(a.purchase_date) || "-",
      val: fmtIDR(a.purchase_value) || "-",
      interval: a.inspection_interval_days ? `${a.inspection_interval_days} hari` : "-",
      lastInsp: fmtDate(a.last_inspection_date) || "-",
      status: STATUS_LABEL[a.status] || a.status || "-",
      plate: (a.vehicle || {}).plate_number || "",
    };
  });
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const title = opts.title || "Kartu Aset";
  const printedAt = new Date().toLocaleString("id-ID");
  // 2 kartu horizontal per A4 landscape. Kartu ≈ 148×200mm (2 kolom).
  const cardHtml = (c) => `
    <div class="card">
      <div class="hdr">
        <div class="no">${esc(c.no)}</div>
        <div class="badge">${esc(c.cat)}${c.plate ? " · " + esc(c.plate) : ""}</div>
      </div>
      <div class="name">${esc(c.name)}</div>
      <div class="grid">
        <div class="k">Brand / Tipe</div><div class="v">${esc(c.brand)}</div>
        <div class="k">Serial</div><div class="v">${esc(c.serial)}</div>
        <div class="k">Lokasi</div><div class="v">${esc(c.loc)}</div>
        <div class="k">PIC</div><div class="v">${esc(c.pic)}</div>
        <div class="k">Tgl Beli</div><div class="v">${esc(c.buy)}</div>
        <div class="k">Nilai</div><div class="v">${esc(c.val)}</div>
        <div class="k">Interval Pemeriksaan</div><div class="v">${esc(c.interval)}</div>
        <div class="k">Pemeriksaan Terakhir</div><div class="v">${esc(c.lastInsp)}</div>
        <div class="k">Status</div><div class="v"><span class="st">${esc(c.status)}</span></div>
      </div>
      <div class="ftr">${esc(title)} · ${esc(printedAt)}</div>
    </div>`;
  // Group cards ke pasangan (2 per halaman)
  const pages = [];
  for (let i = 0; i < cards.length; i += 2) {
    pages.push(cards.slice(i, i + 2));
  }
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  html, body { margin: 0; padding: 0; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #0B1D3A; }
  .page { display: grid; grid-template-columns: 1fr 1fr; gap: 6mm; page-break-after: always; height: calc(210mm - 16mm); }
  .page:last-child { page-break-after: auto; }
  .card { border: 1.5px solid #0B1D3A; padding: 8mm; display: flex; flex-direction: column; break-inside: avoid; box-sizing: border-box; }
  .hdr { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 2px solid #FF5A1F; padding-bottom: 4mm; margin-bottom: 4mm; }
  .no { font-family: monospace; font-size: 20px; font-weight: bold; letter-spacing: 0.02em; }
  .badge { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; background: #FF5A1F; color: #fff; padding: 2px 8px; }
  .name { font-size: 16px; font-weight: 600; margin-bottom: 6mm; line-height: 1.3; }
  .grid { display: grid; grid-template-columns: 45mm 1fr; gap: 3mm 6mm; font-size: 11px; flex: 1; }
  .k { color: #64748b; text-transform: uppercase; font-size: 9px; letter-spacing: 0.06em; padding-top: 2px; }
  .v { color: #0B1D3A; font-weight: 500; word-break: break-word; }
  .st { display: inline-block; padding: 1px 6px; background: #ecfdf5; color: #065f46; border: 1px solid #10b981; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  .ftr { font-size: 8px; color: #94a3b8; text-align: right; margin-top: 4mm; border-top: 1px dashed #cbd5e1; padding-top: 2mm; }
</style></head><body>
${pages.map((pair) => `<div class="page">${pair.map(cardHtml).join("")}${pair.length === 1 ? '<div></div>' : ""}</div>`).join("")}
<script>window.onload=()=>{setTimeout(()=>window.print(),200);};</script>
</body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
}
